import React, { useCallback, useEffect, useRef, useState } from 'react';
import type { PhotoFieldConfig } from '@troia-v3/types';

import { Button, FileDropzone, Modal, ModalContent, ModalFooter, ModalHeader } from '@/shared/components/ui';
import { Icon } from '@/shared/icons';
import { api } from '@/lib/api';

/** Teto de fotos quando o modelo não define `photoConfig.maxPhotos` (spec §7). */
const DEFAULT_MAX_PHOTOS = 5;

/** Qualidade do JPEG gerado pelo canvas na captura in-app. */
const CAPTURE_QUALITY = 0.85;

/**
 * Sobe um arquivo para `POST /api/uploads?folder=checklists` (multipart, 16MB,
 * autenticado) e devolve a URL pública. Molde: `shared/components/ui/UploadInput.tsx:36`.
 *
 * Exportado porque a `ChecklistFillPage` reusa exatamente o mesmo caminho para
 * o campo `file_upload` — um único ponto que conhece a rota de upload do módulo.
 */
export const uploadChecklistFile = async (file: File): Promise<string> => {
  const formData = new FormData();
  formData.append('file', file);
  const response = await api.post<{ success: boolean; data: { url: string } }>(
    '/uploads?folder=checklists',
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  );
  const url = response.data?.data?.url;
  if (!url) throw new Error('Upload não retornou URL');
  return url;
};

/** `true` quando a captura in-app é possível (exige contexto seguro — HTTPS/localhost). */
const canUseCamera = (): boolean =>
  typeof navigator !== 'undefined' &&
  !!navigator.mediaDevices &&
  typeof navigator.mediaDevices.getUserMedia === 'function';

/* ------------------------------------------------------------------ */
/* Modal de captura in-app                                             */
/* ------------------------------------------------------------------ */

interface CameraCaptureModalProps {
  fieldId: string;
  onCapture: (file: File) => void;
  onClose: () => void;
  /** Fallback: abre o seletor nativo (`capture="environment"`) do componente pai. */
  onPickFile: () => void;
}

/**
 * Modal de câmera — montado SOMENTE enquanto aberto.
 *
 * Essa é a decisão central do ciclo de vida: como o componente só existe
 * enquanto a câmera está aberta, o `return` do `useEffect` é o ÚNICO ponto de
 * parada das tracks e ele roda em toda saída possível — botão fechar, ESC do
 * `Modal` shared, captura concluída, navegação para outra rota (desmonta a
 * página inteira) ou re-render do pai que tire o modal da árvore. Sem isso o
 * LED da câmera fica aceso e o Android segura o device para outros apps.
 */
const CameraCaptureModal: React.FC<CameraCaptureModalProps> = ({ fieldId, onCapture, onClose, onPickFile }) => {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [ready, setReady] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const start = async (): Promise<void> => {
      if (!canUseCamera()) {
        setFailed(true);
        return;
      }
      try {
        // `environment` = câmera traseira (é a que aponta para o que está sendo
        // inspecionado); em desktop sem traseira o browser cai na única disponível.
        const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } });
        if (cancelled) {
          // Permissão concedida DEPOIS do fechamento: o cleanup já rodou e não
          // viu este stream — paramos aqui, senão a câmera fica ligada órfã.
          stream.getTracks().forEach((track) => track.stop());
          return;
        }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          // Safari/iOS não inicia só com `autoPlay`; o catch evita unhandled rejection
          // quando o elemento é desmontado no meio do play().
          void videoRef.current.play().catch(() => undefined);
        }
        setReady(true);
      } catch {
        // Permissão negada, sem dispositivo, ou origem insegura.
        if (!cancelled) setFailed(true);
      }
    };

    void start();

    return () => {
      cancelled = true;
      streamRef.current?.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
      if (videoRef.current) videoRef.current.srcObject = null;
    };
  }, []);

  const handleShutter = useCallback((): void => {
    const video = videoRef.current;
    if (!video || !video.videoWidth || !video.videoHeight) return;

    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const context = canvas.getContext('2d');
    if (!context) return;
    context.drawImage(video, 0, 0, canvas.width, canvas.height);

    canvas.toBlob(
      (blob) => {
        if (!blob) return;
        onCapture(new File([blob], `checklist-${Date.now()}.jpg`, { type: 'image/jpeg' }));
      },
      'image/jpeg',
      CAPTURE_QUALITY,
    );
  }, [onCapture]);

  return (
    <Modal isOpen onClose={onClose} maxWidth="lg">
      <ModalHeader title="Tirar foto" onClose={onClose} />
      <ModalContent>
        {failed ? (
          <div className="text-center py-6" data-testid={`photo-camera-${fieldId}-fallback`}>
            <Icon name="camera" className="w-8 h-8 mx-auto mb-3" style={{ color: 'var(--theme-muted-text)' }} />
            <p className="text-sm font-semibold" style={{ color: 'var(--theme-text)' }}>
              Não foi possível abrir a câmera
            </p>
            <p className="text-xs mt-1 mb-4" style={{ color: 'var(--theme-muted-text)' }}>
              Verifique a permissão do navegador. Você ainda pode usar a câmera do aparelho pelo seletor de arquivos.
            </p>
            <Button
              variant="primary"
              icon="camera"
              size="lg"
              onClick={onPickFile}
              data-testid={`photo-camera-${fieldId}-fallback-pick`}
            >
              Usar a câmera do aparelho
            </Button>
          </div>
        ) : (
          <div className="relative rounded-xl overflow-hidden" style={{ backgroundColor: '#000000' }}>
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              className="w-full max-h-[60vh] object-contain"
              data-testid={`photo-camera-${fieldId}-video`}
            />
            {!ready && (
              <div className="absolute inset-0 flex items-center justify-center">
                <Icon name="loader" className="w-7 h-7 animate-spin" style={{ color: '#ffffff' }} />
              </div>
            )}
          </div>
        )}
      </ModalContent>
      {!failed && (
        <ModalFooter>
          <Button variant="secondary" size="lg" onClick={onClose} data-testid={`photo-camera-${fieldId}-cancel`}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            icon="camera"
            size="lg"
            disabled={!ready}
            onClick={handleShutter}
            data-testid={`photo-camera-${fieldId}-shutter`}
          >
            Capturar
          </Button>
        </ModalFooter>
      )}
    </Modal>
  );
};

CameraCaptureModal.displayName = 'CameraCaptureModal';

/* ------------------------------------------------------------------ */
/* Campo de fotos                                                      */
/* ------------------------------------------------------------------ */

interface PendingUpload {
  key: string;
  file: File;
  status: 'uploading' | 'error';
}

export interface PhotoCaptureFieldProps {
  fieldId: string;
  config?: PhotoFieldConfig;
  /** URLs já enviadas — é o valor que vai para `FormAnswer.value`. */
  value: string[];
  onChange: (urls: string[]) => void;
  /**
   * Uploads AINDA EM VOO neste campo (não inclui os que falharam — esses têm
   * retry/descartar visíveis no tile).
   *
   * Existe porque a foto só entra em `value` DEPOIS que o upload responde: sem
   * esse sinal, tocar "Enviar" 1s após a última foto mandaria o checklist sem
   * ela, com toast de sucesso e nenhum aviso. Quem consome (`ChecklistFillPage`)
   * soma os campos e bloqueia o envio enquanto houver algo em voo.
   */
  onPendingChange?: (count: number) => void;
}

/**
 * PhotoCaptureField — captura de fotos do checklist em campo.
 *
 * Dois modos, decididos pelo `photoConfig.source` do modelo:
 * - `any`: `FileDropzone` shared (`accept="image/*"`, múltiplo) — no celular o
 *   seletor nativo já oferece a câmera; no desktop, arrastar e soltar.
 * - `camera_only`: botão primário grande abre a câmera IN-APP (`getUserMedia` +
 *   `<video>` + `canvas.toBlob`). Quando `getUserMedia` não existe (origem
 *   insegura) ou é negado, cai para `<input type="file" capture="environment">`
 *   — que abre a câmera direto no mobile. O `capture` é uma DICA do padrão, não
 *   um bloqueio: um usuário determinado ainda consegue escolher da galeria.
 *   Travar isso de verdade exigiria app nativo (spec §7 aceita a limitação).
 *
 * O input nativo NUNCA aparece cru: fica `hidden` e é acionado por Button/
 * FileDropzone shared (CLAUDE.md NUNCA #9).
 */
export const PhotoCaptureField: React.FC<PhotoCaptureFieldProps> = ({
  fieldId,
  config,
  value,
  onChange,
  onPendingChange,
}) => {
  const [pending, setPending] = useState<PendingUpload[]>([]);
  const [cameraOpen, setCameraOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const maxPhotos = config?.maxPhotos ?? DEFAULT_MAX_PHOTOS;
  const cameraOnly = config?.source === 'camera_only';

  // Espelhos das props: dois uploads concorrentes podem resolver antes do
  // re-render do pai, e ler `value`/`onChange` do closure perderia a foto do
  // primeiro. As refs são atualizadas SÍNCRONAMENTE no `commit`.
  const valueRef = useRef(value);
  const onChangeRef = useRef(onChange);
  const onPendingChangeRef = useRef(onPendingChange);
  useEffect(() => {
    valueRef.current = value;
  }, [value]);
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);
  useEffect(() => {
    onPendingChangeRef.current = onPendingChange;
  }, [onPendingChange]);

  const commit = useCallback((next: string[]): void => {
    valueRef.current = next;
    onChangeRef.current(next);
  }, []);

  const runUpload = useCallback(
    async (key: string, file: File): Promise<void> => {
      try {
        const url = await uploadChecklistFile(file);
        commit([...valueRef.current, url]);
        setPending((prev) => prev.filter((item) => item.key !== key));
      } catch {
        // Sem toast: o interceptor global de `@/lib/api` já avisa. O tile fica
        // em estado de erro com "Tentar de novo" — o gerente em campo perde
        // sinal o tempo todo e precisa do retry manual, não de um upload
        // silenciosamente descartado.
        setPending((prev) => prev.map((item) => (item.key === key ? { ...item, status: 'error' } : item)));
      }
    },
    [commit],
  );

  const enqueue = useCallback(
    (files: File[]): void => {
      const slots = maxPhotos - (valueRef.current.length + pending.length);
      const accepted = files.slice(0, Math.max(slots, 0));
      if (accepted.length === 0) return;

      const items: PendingUpload[] = accepted.map((file, index) => ({
        key: `${Date.now()}-${index}-${Math.random().toString(36).slice(2, 8)}`,
        file,
        status: 'uploading',
      }));
      setPending((prev) => [...prev, ...items]);
      items.forEach((item) => void runUpload(item.key, item.file));
    },
    [maxPhotos, pending.length, runUpload],
  );

  const handleCapture = useCallback(
    (file: File): void => {
      setCameraOpen(false); // desmonta o modal → cleanup para as tracks
      enqueue([file]);
    },
    [enqueue],
  );

  const openNativePicker = useCallback((): void => {
    setCameraOpen(false);
    fileInputRef.current?.click();
  }, []);

  /**
   * Clique no botão "Tirar foto": quando não há `getUserMedia` (HTTP puro,
   * WebView antiga) nem abrimos o modal — vamos direto ao seletor nativo, ainda
   * DENTRO do gesto do usuário (browsers bloqueiam `input.click()` programático
   * fora de um gesto, então isso não poderia ser feito depois de um `await`).
   */
  const handleCameraButton = useCallback((): void => {
    if (canUseCamera()) setCameraOpen(true);
    else fileInputRef.current?.click();
  }, []);

  const removePhoto = useCallback(
    (index: number): void => {
      commit(valueRef.current.filter((_, position) => position !== index));
    },
    [commit],
  );

  const retry = useCallback(
    (key: string): void => {
      const item = pending.find((entry) => entry.key === key);
      if (!item) return;
      setPending((prev) => prev.map((entry) => (entry.key === key ? { ...entry, status: 'uploading' } : entry)));
      void runUpload(key, item.file);
    },
    [pending, runUpload],
  );

  const discard = useCallback((key: string): void => {
    setPending((prev) => prev.filter((entry) => entry.key !== key));
  }, []);

  const total = value.length + pending.length;
  const canAdd = total < maxPhotos;
  const inFlight = pending.filter((item) => item.status === 'uploading').length;
  const uploading = inFlight > 0;

  // Reporta uploads em voo pelo ref (a prop pode ser recriada a cada render do
  // pai; depender dela aqui dispararia o efeito à toa).
  useEffect(() => {
    onPendingChangeRef.current?.(inFlight);
  }, [inFlight]);

  // Desmontagem: zera a contagem, senão o pai bloquearia o envio para sempre
  // por causa de um campo que nem existe mais na árvore.
  useEffect(() => () => onPendingChangeRef.current?.(0), []);

  return (
    <div data-testid={`photo-capture-${fieldId}`}>
      {(value.length > 0 || pending.length > 0) && (
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 mb-3">
          {value.map((url, index) => (
            <div
              key={`${url}-${index}`}
              className="relative aspect-square rounded-lg overflow-hidden"
              style={{ border: '1px solid var(--theme-card-border)' }}
              data-testid={`photo-thumb-${fieldId}-${index}`}
            >
              <img src={url} alt={`Foto ${index + 1}`} className="w-full h-full object-cover" />
              <button
                type="button"
                onClick={() => removePhoto(index)}
                className="absolute top-1 right-1 rounded-full flex items-center justify-center"
                style={{ width: 28, height: 28, backgroundColor: 'rgba(0,0,0,0.6)', color: '#ffffff' }}
                aria-label={`Remover foto ${index + 1}`}
                data-testid={`photo-thumb-${fieldId}-${index}-remove`}
              >
                <Icon name="x" className="w-4 h-4" />
              </button>
            </div>
          ))}

          {pending.map((item) => (
            <div
              key={item.key}
              className="relative aspect-square rounded-lg flex flex-col items-center justify-center gap-1 text-center px-1"
              style={{
                border: '1px dashed var(--theme-input-border)',
                backgroundColor: 'var(--theme-surface-sunken)',
              }}
              data-testid={`photo-pending-${fieldId}-${item.key}`}
            >
              {item.status === 'uploading' ? (
                <>
                  <Icon name="loader" className="w-5 h-5 animate-spin" style={{ color: 'var(--theme-muted-text)' }} />
                  <span className="text-[11px]" style={{ color: 'var(--theme-muted-text)' }}>
                    Enviando…
                  </span>
                </>
              ) : (
                <>
                  <Icon name="alert-triangle" className="w-5 h-5" style={{ color: 'var(--theme-error-fg)' }} />
                  <button
                    type="button"
                    onClick={() => retry(item.key)}
                    className="text-[11px] font-semibold underline"
                    style={{ color: 'var(--theme-primary)' }}
                    data-testid={`photo-retry-${fieldId}`}
                  >
                    Tentar de novo
                  </button>
                  <button
                    type="button"
                    onClick={() => discard(item.key)}
                    className="text-[11px]"
                    style={{ color: 'var(--theme-muted-text)' }}
                    data-testid={`photo-discard-${fieldId}`}
                  >
                    Descartar
                  </button>
                </>
              )}
            </div>
          ))}
        </div>
      )}

      {canAdd &&
        (cameraOnly ? (
          <Button
            type="button"
            variant="primary"
            icon="camera"
            size="lg"
            fullWidth
            onClick={handleCameraButton}
            data-testid={`photo-capture-${fieldId}-open`}
          >
            {value.length > 0 ? 'Tirar outra foto' : 'Tirar foto'}
          </Button>
        ) : (
          <FileDropzone
            accept="image/*"
            multiple
            uploading={uploading}
            onFiles={enqueue}
            icon="image"
            label={value.length > 0 ? 'Adicionar mais fotos' : 'Toque para escolher ou arraste fotos'}
            hint={`JPG, PNG ou WebP · até ${maxPhotos} foto${maxPhotos > 1 ? 's' : ''}`}
            showButton
            buttonLabel="Escolher foto"
            data-testid={`photo-capture-${fieldId}-dropzone`}
          />
        ))}

      {!canAdd && (
        <p className="text-xs" style={{ color: 'var(--theme-muted-text)' }} data-testid={`photo-capture-${fieldId}-limit`}>
          Limite de {maxPhotos} foto{maxPhotos > 1 ? 's' : ''} atingido. Remova uma para enviar outra.
        </p>
      )}

      {/* Fallback nativo — sempre `hidden`, acionado só por Button/Modal shared.
          `capture="environment"` abre a câmera traseira direto no mobile. */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        data-testid={`photo-capture-${fieldId}-native-input`}
        onChange={(event) => {
          const file = event.target.files?.[0];
          if (file) enqueue([file]);
          event.target.value = '';
        }}
      />

      {cameraOpen && (
        <CameraCaptureModal
          fieldId={fieldId}
          onCapture={handleCapture}
          onClose={() => setCameraOpen(false)}
          onPickFile={openNativePicker}
        />
      )}
    </div>
  );
};

PhotoCaptureField.displayName = 'PhotoCaptureField';
